import React  from "react";

import { useDispatch, useSelector } from "react-redux";

import {
	downloadCreative,
	downloadCreativeSummary,
	deleteCreatives,
	sendForAutoClassification
} from "reducers/Creatives";

import { fetchCreativesByConcept, deleteConcept } from "reducers/Concepts";
import { confirmDeleteCreatives, confirmDeleteConcept } from "reducers/Confirm";
import {
	shouldDisplayAction,
	canDeleteConcept,
	isTranscoding,
	isApproved
} from 'utils/CreativeActions';
import { getRemainingCredits } from "selectors/Intelligence";
import { isVideoBuilderCreative, noop } from "utils/common";
import { tellSentry } from "utils/Rave";
import { BasicAction, LinkAction } from "widgets/ActionMenu";
import { openModal } from 'reducers/Modal';
import { MODAL_TYPES, NON_AUTOCLASSIFIABLE_ADTYPES } from 'utils/constants';


export function BasicEllipsisAction({ icon, name, className, rowInfo = {} }){
	const dispatch = useDispatch();
	let { libraryId, creativeId } = rowInfo
		, isDisabled = isTranscoding(rowInfo.approval)
		, actionName = name;

	if(typeof name === 'function'){
		actionName =  name(rowInfo);
	}

	let handleClick = () => console.log(`"${actionName}" action isn't implemented.`);

	const ellipsisDispatch = {
		"Rename Creative":            () => dispatch(openModal(MODAL_TYPES.RENAME_CREATIVES, {
			creatives: [{
				id: rowInfo.creativeId,
				name: rowInfo.creativeName,
			}],
			libraryId: rowInfo.libraryId,
			propName: 'name',
		})),
		"Rename Creative Filename":   () => dispatch(openModal(MODAL_TYPES.RENAME_CREATIVES, {
			creatives: [{
				id: rowInfo.creativeId,
				name: rowInfo.creativeName,
				fileName: rowInfo.fileName,
			}],
			libraryId: rowInfo.libraryId,
			propName: 'fileName',
		})),
		"Download Creative":          () => {
			return dispatch(downloadCreative(libraryId, [creativeId], rowInfo.fileName));
		},
		"Download Creative Summary":  () => {
			return dispatch(downloadCreativeSummary(libraryId, [creativeId], rowInfo.summaryFileName));
		},
		"Rename Concept":            () => dispatch(openModal(MODAL_TYPES.RENAME_CONCEPT, {
			conceptId: rowInfo.conceptId,
			conceptName: rowInfo.conceptName,
			libraryId: rowInfo.libraryId,
			creativeId: rowInfo.creativeId,
		})),
		"Duplicate Concept":          () => dispatch(openModal(MODAL_TYPES.DUPLICATE_CONCEPTS, {
			creatives: [{
				id: rowInfo.creativeId,
				name: rowInfo.creativeName,
				approval: rowInfo.approval,
				conceptId: rowInfo.conceptId,
				conceptName: rowInfo.conceptName,
			}],
			creativeId: rowInfo.creativeId, // Creative that issued the action
			libraryId: rowInfo.libraryId,
		})),
		"Delete Creative":            () => dispatch(confirmDeleteCreatives(deleteCreatives, {
			libraryId,
			creatives: [{
				id: rowInfo.creativeId,
				name: rowInfo.creativeName,
				approval: rowInfo.approval,
			}],
		})),
		"Delete Concept":             () => {
			dispatch(confirmDeleteConcept(deleteConcept, false, rowInfo));
			dispatch(fetchCreativesByConcept(rowInfo.conceptId))
				.then((res) => {
					if (!res?.payload?.data?.items) {
						return false;
					}
					let { items } = res.payload.data;
					return canDeleteConcept(items);
				})
				.then((canDelete) => {
				// If we can delete the concept, then re-open the confirm modal, it works trust me
					if(canDelete){
						dispatch(confirmDeleteConcept(deleteConcept, canDelete, rowInfo));
					}
				});
		},
		"Add Existing Creative to Concept": () => dispatch(openModal(MODAL_TYPES.ADD_CREATIVES_TO_CONCEPT, {
			libraryId,
			conceptId: rowInfo.conceptId,
			conceptName: rowInfo.conceptName,
			creativeId: rowInfo.creativeId,
		})),
		"New Ad Studio Creative to Concept": () => {
			dispatch(openModal(MODAL_TYPES.ADD_ADSTUDIO_TO_CONCEPT, {
				libraryId,
				creativeName: rowInfo.creativeName,
				conceptId: rowInfo.conceptId,
				creativeId: rowInfo.creativeId,
			}));
		},
		"New Creative Tags to Concept": () => {
			dispatch(openModal(MODAL_TYPES.ADD_CREATIVE_TAGS_TO_CONCEPT, {
				libraryId,
				creativeName: rowInfo.creativeName,
				conceptId: rowInfo.conceptId,
				creativeId: rowInfo.creativeId,
				conceptName: rowInfo.conceptName,
			}));
		},
		"Remove Creative from Concept": () => {
			dispatch(openModal(MODAL_TYPES.REMOVE_CREATIVES_FROM_CONCEPT, {
				libraryId,
				conceptId: rowInfo.conceptId,
				creatives: [{
					id: rowInfo.creativeId,
					name: rowInfo.creativeName,
					approval: rowInfo.approval,
				}],
			}));
		},
		"View Encodes": () => dispatch(openModal(MODAL_TYPES.VIEW_ENCODES, {
			creatives:[{
				id: rowInfo.creativeId,
				name: rowInfo.creativeName,
				videos: rowInfo.videos,
			}],
		})),
	};

	if(ellipsisDispatch[actionName]){
		handleClick = ellipsisDispatch[actionName];
	}

	return (
		<BasicAction
			iconName={icon}
			name={actionName}
			isDisabled={isDisabled}
			className={className}
			handleClick={handleClick}
		/>
	);
}

BasicEllipsisAction.displayName = "EllipsisAction";

export function BasicLinkAction({ icon, name, pathFn = noop, rowInfo }){
	let path = pathFn(rowInfo);

	if(!path){
		tellSentry(`Missing or invalid link for row item: ${name}`);
	}

	return (
		<LinkAction
			iconName={icon}
			name={name}
			useMenuItem
			className="action-link action-item"
			path={path} />
	);
}

const RENAME_CREATIVE = {
	icon: "fas fa-pencil",
	name:  "Rename Creative",
	className: "rename-creative",
	shouldDisplay:({ user }) => shouldDisplayAction.renameCreative(user),
	component: ({ dependentValues }) =>  <BasicEllipsisAction {...RENAME_CREATIVE} rowInfo={dependentValues} />,
};

const RENAME_CREATIVE_FILENAME = {
	icon:"fas fa-pencil",
	name: "Rename Creative Filename",
	className: "rename-creative-filename",
	shouldDisplay:({ user, dependentValues }) => shouldDisplayAction.renameCreativeFilename(user, dependentValues),
	component: ({ dependentValues }) =>  <BasicEllipsisAction {...RENAME_CREATIVE_FILENAME} rowInfo={dependentValues} />,
};

export const DUPLICATE_CREATIVE = {
	icon:"fas fa-clone",
	name(dependentValues){
		return dependentValues.isBulk ? 'Duplicate Creatives' : 'Duplicate Creative';
	},
	className: "duplicate-creative",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.duplicateCreative(user, dependentValues),
	component: ({ dependentValues }) =>  {
		const {
			isBulk,
			creativeId,
			creativeName,
			approval,
			adType,
			dynamicTarget,
			libraryId,
		} = dependentValues || {};

		const dispatch = useDispatch();
		const handleClick = () => {
			dispatch(openModal(MODAL_TYPES.DUPLICATE_CREATIVES, {
				creatives: [
					{
						id: creativeId,
						name: creativeName,
						approval,
						adType,
						dynamicTarget,
					}
				],
				libraryId,
			}));
		};
		const isDisabled = isTranscoding(approval) || isVideoBuilderCreative(dependentValues);
		return (
			<BasicAction
				isDisabled = {isDisabled}
				iconName = "fas fa-clone"
				name = {isBulk ? 'Duplicate Creatives' : 'Duplicate Creative'}
				className = "duplicate-creative"
				handleClick = {handleClick}
			/>
		);
	},
};

export const DOWNLOAD_CREATIVE = {
	icon:"download",
	name: "Download Creative",
	className: "download-creative",
	shouldDisplay:({ user, dependentValues }) => shouldDisplayAction.download(user, dependentValues),
	component:({ dependentValues }) => <BasicEllipsisAction {...DOWNLOAD_CREATIVE} rowInfo={dependentValues} />,
};

export const DOWNLOAD_CREATIVE_SUMMARY = {
	icon:"download",
	name: "Download Creative Summary",
	className: "download-creative",
	component: ({ dependentValues }) => <BasicEllipsisAction {...DOWNLOAD_CREATIVE_SUMMARY} rowInfo={dependentValues} />,
};

const RENAME_CONCEPT = {
	icon: "fas fa-pencil",
	name: "Rename Concept",
	className: "rename-concept",
	shouldDisplay:({ user, dependentValues }) => shouldDisplayAction.renameConcept(user, dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction { ...RENAME_CONCEPT } rowInfo={dependentValues} />,
};

const ADD_SOCIAL_CREATIVE_TO_CONCEPT = {
	icon:"add",
	name:"Add Social Creative to Concept",
	pathFn: dependentValues => `/library/${dependentValues.libraryId}/modal/add-to-concept/social/${dependentValues.conceptId}/`,
	shouldDisplay:({ user, dependentValues }) => shouldDisplayAction.addSocialCreativeToConcept(user, dependentValues),
	component: ({ dependentValues }) => <BasicLinkAction { ...ADD_SOCIAL_CREATIVE_TO_CONCEPT } rowInfo={dependentValues} />,
};

export const DUPLICATE_CONCEPT = {
	icon:"fas fa-clone",
	name: "Duplicate Concept",
	className:"duplicate-concept",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.duplicateConcept(user, dependentValues),
	component: ({ dependentValues }) =>  <BasicEllipsisAction {...DUPLICATE_CONCEPT} rowInfo={dependentValues} />,
};

const NEW_ADSTUDIO_CREATIVE_TO_CONCEPT = {
	icon: "fas fa-palette",
	name: "New Ad Studio Creative to Concept",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.newAdStudioCreativeToConcept(user, dependentValues),
	component: ({ dependentValues }) =>  <BasicEllipsisAction {...NEW_ADSTUDIO_CREATIVE_TO_CONCEPT} rowInfo={dependentValues} />,
};

const NEW_CREATIVE_TAGS_TO_CONCEPT = {
	icon: "fas fa-code",
	name: "New Creative Tags to Concept",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.newPartnerTagToConcept(user, dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction {...NEW_CREATIVE_TAGS_TO_CONCEPT} rowInfo={dependentValues} />,
};

const ADD_EXISTING_CREATIVE_TO_CONCEPT = {
	icon:"fas fa-plus",
	name:"Add Existing Creative to Concept",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.addExistingCreativeToConcept(user, dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction {...ADD_EXISTING_CREATIVE_TO_CONCEPT} rowInfo={dependentValues} />,
};

export const DELETE_CREATIVE = {
	icon:"delete",
	name: "Delete Creative",
	className: "delete-creative",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.deleteCreative(user, dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction {...DELETE_CREATIVE} rowInfo={dependentValues} />,
};

const DELETE_CONCEPT = {
	icon:"delete",
	name: "Delete Concept",
	className: "delete-concept",
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.deleteConcept(user, dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction {...DELETE_CONCEPT} rowInfo={dependentValues} />,
};

const SELECT_NEW_VIDEO = {
	icon:"fas fa-video-plus",
	name:"Select New Video",
	pathFn:dependentValues=> `/library/${dependentValues.libraryId}/modal/replace-vast/${dependentValues.creativeId}/`,
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.selectNewVideo(user, dependentValues),
	component: ({ dependentValues }) => <BasicLinkAction { ...SELECT_NEW_VIDEO } rowInfo={dependentValues} />,
};

const VIDEO_SPECS = {
	icon:"fas fa-file-alt",
	name:"Video Specs",
	pathFn:dependentValues=> `/library/${dependentValues?.libraryId}/modal/video-specs/${dependentValues?.creativeId}/`,
	shouldDisplay: ({ user, dependentValues }) => shouldDisplayAction.videoSpecs(user, dependentValues),
	component: ({ dependentValues }) => <BasicLinkAction { ...VIDEO_SPECS } rowInfo={dependentValues} />,
};

const REMOVE_CREATIVE_FROM_CONCEPT = {
	icon: "fas fa-minus",
	name:  "Remove Creative from Concept",
	shouldDisplay:({ user, dependentValues }) => shouldDisplayAction.removeCreativeFromConcept(user, dependentValues),
	component: ({ dependentValues }) =>  <BasicEllipsisAction {...REMOVE_CREATIVE_FROM_CONCEPT} rowInfo={dependentValues} />,
};
export const VIEW_ENCODES = {
	icon:"fas fa-video",
	name:"View Encodes",
	bulkAction:false,
	shouldDisplay: ({ dependentValues }) => shouldDisplayAction.encodeView(dependentValues),
	component: ({ dependentValues }) => <BasicEllipsisAction {...VIEW_ENCODES} rowInfo={dependentValues} />,
};

export const CLASSIFY = {
	icon: "fas fa-tags",
	name: "Send for Auto-Classification",
	shouldDisplay: ({ dependentValues, isAutoClassificationEnabled }) => {
		if(!isAutoClassificationEnabled){
			return false;
		}

		const { approval, adType } = dependentValues || {};
		const isValidType = !NON_AUTOCLASSIFIABLE_ADTYPES.includes(adType);

		return isApproved(approval) && isValidType;
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const { libraryId, creativeId } = dependentValues || {};
		const credits = useSelector(getRemainingCredits);
		function handleClick(){
			dispatch(sendForAutoClassification(libraryId, [creativeId]));
		}
		return (
			<BasicAction
				iconName={CLASSIFY.icon}
				name={CLASSIFY.name}
				isDisabled={credits === 0}
				handleClick={handleClick}
			/>
		);
	},
};

/**
 * Each array inside the list makes a divider on the grid actions
 */
export const ELLIPSIS_ACTION_LIST = [
	[
		RENAME_CREATIVE,
		RENAME_CREATIVE_FILENAME,
		DUPLICATE_CREATIVE,
		DOWNLOAD_CREATIVE,
		DOWNLOAD_CREATIVE_SUMMARY,
	],
	[
		VIEW_ENCODES,
	],
	[
		RENAME_CONCEPT,
		ADD_SOCIAL_CREATIVE_TO_CONCEPT,
		NEW_ADSTUDIO_CREATIVE_TO_CONCEPT,
		NEW_CREATIVE_TAGS_TO_CONCEPT,
		ADD_EXISTING_CREATIVE_TO_CONCEPT,
		REMOVE_CREATIVE_FROM_CONCEPT,
		DUPLICATE_CONCEPT,
	],
	[
		CLASSIFY,
	],
	[
		DELETE_CREATIVE,
		DELETE_CONCEPT,
	],
];

export const TRANSCODING_ELLIPSIS_ACTION_LIST=[
	[
		RENAME_CREATIVE,
	],
	[
		DELETE_CREATIVE,
	],
];

export const TRANSCODED_ELLIPSIS_ACTION_LIST=[
	[
		RENAME_CREATIVE,
		DUPLICATE_CREATIVE,
		DOWNLOAD_CREATIVE,
		DOWNLOAD_CREATIVE_SUMMARY,
	],
	[
		SELECT_NEW_VIDEO,
	],
	[
		CLASSIFY,
	],
	[
		DELETE_CREATIVE,
	]
];

export const TRANSCODED_DVAST_ELLIPSIS_ACTION_LIST = [
	[
		RENAME_CREATIVE,
		DUPLICATE_CREATIVE,
		DOWNLOAD_CREATIVE,
		DOWNLOAD_CREATIVE_SUMMARY,
	],
	[
		VIDEO_SPECS,
	],
	[
		SELECT_NEW_VIDEO,
	],
	[
		CLASSIFY,
	],
	[
		DELETE_CREATIVE,
	],
];

export const PRIVATE ={
	ELLIPSIS_ACTION_LIST,
	RENAME_CREATIVE,
	RENAME_CREATIVE_FILENAME,
	DUPLICATE_CREATIVE,
	DOWNLOAD_CREATIVE,
	DOWNLOAD_CREATIVE_SUMMARY,
	RENAME_CONCEPT,
	ADD_SOCIAL_CREATIVE_TO_CONCEPT,
	NEW_ADSTUDIO_CREATIVE_TO_CONCEPT,
	ADD_EXISTING_CREATIVE_TO_CONCEPT,
	DUPLICATE_CONCEPT,
	DELETE_CREATIVE,
	DELETE_CONCEPT,
};
